import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { eventService } from "@project/services/admin/event.service";
import { eventCategoryService } from "@project/services/admin/event-category.service";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import {
  Calendar,
  MapPin,
  Clock,
  HeartHandshake,
  Search,
  Ticket,
  Eye,
  Filter,
} from "lucide-react";
import { Reveal } from "@project/user/components/reveal";
import { useI18n } from "@project/lib/use-i18n";
import {
  HERO_IMAGES,
  FALLBACK_EVENT_BANNER,
  pickFrom,
} from "@project/lib/stock-images";
import { EventDetailModal } from "@project/user/components/event-detail-modal";
import { EventRegistrationModal } from "@project/user/components/event-registration-modal";
import type { Event as ApiEvent } from "@project/types/event";

// Format time string e.g. "09:00:00" -> "09:00"
function formatDisplayTime(timeStr?: string | null): string {
  if (!timeStr) {
    return "";
  }
  const parts = timeStr.split(":");
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return timeStr;
}

// Format date string e.g. "2026-07-30" -> "30-07-2026"
function formatBadgeDate(dateStr?: string | null): string {
  if (!dateStr) {
    return "";
  }
  const clean = dateStr.split("T")[0];
  const parts = clean.split("-");
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
}

// Sample fallback community events to guarantee rich UI display matching API schema
const SAMPLE_EVENTS: ApiEvent[] = [
  {
    id: 1,
    category_name: "Dharma & Pravachan",
    event_title: "Free Health Check-up & Medical Camp",
    status: "Published",
    date: "2026-08-20",
    start_date: "2026-08-20",
    end_date: "2026-08-20",
    start_time: "09:00:00",
    end_time: "17:00:00",
    venue: "Jivandhara Medical Room",
    address: "Jivandhara Campus, Palitana",
    max_seat_capacity: 150,
    short_summary:
      "Multi-specialty health camp for elders — eye, dental, and general check-up.",
    description:
      "In collaboration with leading ophthalmic surgeons and medical specialists, Jivandhara is organizing a full-day health checkup and vision camp.",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1000&q=80",
    category: 1,
  },
  {
    id: 2,
    category_name: "Cultural & Festivals",
    event_title: "Mahavir Jayanti Annadan",
    status: "Published",
    date: "2026-09-14",
    start_date: "2026-09-14",
    end_date: "2026-09-14",
    start_time: "10:00:00",
    end_time: "14:00:00",
    venue: "Jivandhara Campus, Ahmedabad",
    address: "Ahmedabad, Gujarat",
    max_seat_capacity: 300,
    short_summary:
      "Community Annadan on the auspicious occasion of Mahavir Jayanti.",
    description:
      "Grand community food distribution drive honoring Lord Mahavir's philosophy of non-violence, benevolence, and seva.",
    image:
      "https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&w=1000&q=80",
    category: 2,
  },
  {
    id: 3,
    category_name: "Dharma & Pravachan",
    event_title: "Paryushan Parva Pravachan & Anumodna",
    status: "Published",
    date: "2026-10-14",
    start_date: "2026-10-14",
    end_date: "2026-10-21",
    start_time: "06:00:00",
    end_time: "20:00:00",
    venue: "Jivandhara Prayer Hall",
    address: "Palitana, Gujarat",
    max_seat_capacity: 200,
    short_summary: "Spiritual discourses and special meals during Paryushan.",
    description:
      "Eight sacred days of Pravachans, daily Jin Pooja, Tapasya anumodna, Samvatsari pratikraman, and holy Bhakti Bhavana.",
    image:
      "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1000&q=80",
    category: 1,
  },
  {
    id: 4,
    category_name: "Daan Abhiyans",
    event_title: "Gaushala Gras Daan Drive",
    status: "Published",
    date: "2026-11-05",
    start_date: "2026-11-05",
    end_date: "2026-11-05",
    start_time: "08:00:00",
    end_time: "12:00:00",
    venue: "Gir Gaushala, Jivandhara",
    address: "Palitana, Gujarat",
    max_seat_capacity: 100,
    short_summary:
      "Green fodder & nutrition distribution drive for resident Gir cows.",
    description:
      "Monthly grass feeding program and Gau Pujan ritual conducted by scholars with active community participation.",
    image:
      "https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=1000&q=80",
    category: 4,
  },
];

export default function EventsPage() {
  const { t } = useI18n();

  // State management
  const [activeTab, setActiveTab] = useState<"upcoming" | "past">("upcoming");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Modal controls
  const [detailEvent, setDetailEvent] = useState<any | null>(null);
  const [rsvpEvent, setRsvpEvent] = useState<any | null>(null);

  // Construct dynamic API query params from user filter selections
  const apiQueryParams = useMemo(() => {
    const queryParams: Record<string, any> = { page_size: 100 };

    if (searchQuery.trim()) {
      queryParams.search = searchQuery.trim();
    }

    if (selectedCategory && selectedCategory !== "all") {
      queryParams.category = selectedCategory;
    }

    if (activeTab === "upcoming") {
      queryParams.status = "Published";
    } else if (activeTab === "past") {
      queryParams.status = "Completed";
    }

    return queryParams;
  }, [searchQuery, selectedCategory, activeTab]);

  // Fetch events list dynamically from Backend API service with current filter parameters
  const { data: apiEvents, isLoading: isEventsLoading } = useQuery({
    queryKey: ["public-events-list", apiQueryParams],
    queryFn: async () => {
      try {
        const res = await eventService.listPage(apiQueryParams);
        return res?.data ?? [];
      } catch (err) {
        console.error("Failed to fetch events from API:", err);
        return [];
      }
    },
  });

  // Fetch categories list from Backend API service
  const { data: apiCategories } = useQuery({
    queryKey: ["public-event-categories-list"],
    queryFn: async () => {
      try {
        const res = await eventCategoryService.listPage({ page_size: 50 });
        return res?.data ?? [];
      } catch (err) {
        console.error("Failed to fetch event categories:", err);
        return [];
      }
    },
  });

  // Combine fetched API events with sample events fallback if API returns empty
  const eventsList = useMemo(() => {
    if (apiEvents && apiEvents.length > 0) {
      return apiEvents;
    }
    // Filter sample fallback events according to current user filters
    const today = new Date().toISOString().split("T")[0];
    return SAMPLE_EVENTS.filter((evt: any) => {
      const evtDate = evt.end_date ?? evt.start_date ?? evt.date ?? "";
      const isPast =
        (evtDate && evtDate < today) ??
        evt.status?.toLowerCase() === "completed";
      const matchesTab = activeTab === "upcoming" ? !isPast : isPast;

      let matchesCategory = selectedCategory === "all";
      if (!matchesCategory) {
        const evtCatName = (evt.category_name ?? "").toLowerCase();
        const evtCatId = String(evt.category ?? "").toLowerCase();
        const targetSel = selectedCategory.toLowerCase();
        matchesCategory =
          evtCatId === targetSel || evtCatName.includes(targetSel);
      }

      const query = searchQuery.toLowerCase().trim();
      const matchesSearch =
        (!query || evt.event_title?.toLowerCase().includes(query)) ??
        evt.venue?.toLowerCase().includes(query) ??
        evt.short_summary?.toLowerCase().includes(query);

      return matchesTab && matchesCategory && matchesSearch;
    });
  }, [apiEvents, activeTab, selectedCategory, searchQuery]);

  // Combine category list options (All Events + dynamic API categories / default fallback)
  const categoryOptions = useMemo(() => {
    const defaults = [
      { id: "all", name: t("community.events.categories.all") || "All Events" },
      { id: "Dharma & Pravachan", name: "Dharma & Pravachan" },
      { id: "Health Camps", name: "Health Camps" },
      { id: "Cultural & Festivals", name: "Cultural & Festivals" },
      { id: "Daan Abhiyans", name: "Daan Abhiyans" },
    ];

    if (apiCategories && apiCategories.length > 0) {
      const items = apiCategories.map((c: any) => ({
        id: String(c.id ?? c.category_name),
        name: c.category_name,
      }));
      return [
        {
          id: "all",
          name: t("community.events.categories.all") || "All Events",
        },
        ...items,
      ];
    }

    return defaults;
  }, [apiCategories, t]);

  return (
    <PageShell>
      {/* Banner / Hero Section */}
      <PageHero
        eyebrow={t("community.events.hero.eyebrow")}
        title={t("community.events.hero.title")}
        subtitle={
          t("community.events.hero.subtitle") ||
          "Upcoming pravachans, festivals and donation drives at Jivandhara."
        }
        image={HERO_IMAGES.events}
      />

      <div className="mx-auto min-h-[70vh] max-w-7xl space-y-8 bg-[#FAF8F5]/60 px-4 py-8 md:py-12">
        {/* Subtitle & Golden Accent Line */}
        {/* <div className="text-center space-y-2 -mt-2">
          <p className="text-sm font-semibold text-[#6B5A4B] md:text-base tracking-tight">
            Upcoming pravachans, festivals and donation drives at Jivandhara.
          </p>
          <div className="mx-auto h-[2px] w-24 bg-gradient-to-r from-transparent via-[#D97706] to-transparent rounded-full opacity-80" />
        </div> */}

        {/* 1. Centered Status Tabs Bar (Upcoming Events vs Past Events) */}
        <div className="flex justify-center pb-2">
          <div className="flex w-full max-w-4xl justify-center overflow-x-auto px-2 pb-1">
            <div className="shadow-xs inline-flex h-auto w-max min-w-0 flex-nowrap items-center justify-center gap-1 rounded-full border border-[#DCE4EE] p-1.5 backdrop-blur-sm sm:gap-1.5">
              <button
                type="button"
                onClick={() => setActiveTab("upcoming")}
                className={`whitespace-nowrap rounded-full px-6 py-2 text-xs transition-all duration-200 md:text-sm ${
                  activeTab === "upcoming"
                    ? "shadow-xs bg-gold text-gold-foreground"
                    : "text-muted-foreground hover:bg-[#F3F7FA] hover:text-[#0F4C81]"
                }`}
              >
                Upcoming Events
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("past")}
                className={`whitespace-nowrap rounded-full px-6 py-2 text-xs transition-all duration-200 md:text-sm ${
                  activeTab === "past"
                    ? "shadow-xs bg-gold text-gold-foreground"
                    : "text-muted-foreground hover:bg-[#F3F7FA] hover:text-[#0F4C81]"
                }`}
              >
                Past Events
              </button>
            </div>
          </div>
        </div>

        {/* 2. Main Filter & Search Row Below (Category Pills on Left, Search on Right) */}
        <div className="flex flex-col gap-4 border-b border-[#EAE3D2] pb-5 sm:flex-row sm:items-center sm:justify-between">
          {/* Category Filter Pills on Left */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="mr-1 flex items-center text-amber-600">
              <Filter className="h-4 w-4" />
            </div>

            {categoryOptions.map(cat => {
              const isSelected =
                selectedCategory.toLowerCase() === cat.id.toLowerCase();
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all duration-150 ${
                    isSelected
                      ? "bg-[#0F4C81] text-white shadow-sm"
                      : "bg-[#F3EFE7] text-[#4A5568] hover:bg-[#EBE4D6] hover:text-[#1A202C]"
                  }`}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>

          {/* Search Input Box on Right */}
          <div className="relative w-full shrink-0 sm:w-80">
            <Search className="absolute left-3.5 top-3 h-4 w-4 text-[#8C7A6B]" />
            <Input
              type="text"
              placeholder="Search events or venue..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="shadow-xs rounded-full border-[#E2D9C8] bg-white/90 pl-10 text-xs text-[#2D261E] placeholder:text-[#A09385] focus-visible:ring-amber-500 md:text-sm"
            />
          </div>
        </div>

        {/* Events Grid */}
        {isEventsLoading ? (
          <div className="py-20 text-center text-sm font-semibold text-[#8C7A6B]">
            {t("community.events.loading") || "Loading events..."}
          </div>
        ) : eventsList.length === 0 ? (
          <div className="shadow-xs rounded-3xl border-2 border-dashed border-[#E5DDCB] bg-white p-12 text-center">
            <Calendar className="mx-auto mb-3 h-12 w-12 text-[#C4B6A5]" />
            <p className="text-sm font-semibold text-[#6B5A4B]">
              No events found matching your criteria.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4 rounded-full border-[#D4C8B5] text-xs font-bold text-[#4A3E31]"
              onClick={() => {
                setSelectedCategory("all");
                setSearchQuery("");
              }}
            >
              Reset Filters
            </Button>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {eventsList.map((evt: any, i: number) => {
              const displayTitle =
                evt.event_title ?? evt.title ?? "Jivandhara Event";
              const displayCategory =
                evt.category_name ?? evt.category ?? "Dharma & Pravachan";
              const displayDate = formatBadgeDate(
                evt.start_date ?? evt.date ?? evt.event_date
              );
              const displaySummary =
                evt.short_summary ??
                evt.short_description ??
                evt.description ??
                "";
              const startTime = formatDisplayTime(evt.start_time);
              const endTime = formatDisplayTime(evt.end_time);
              const displayVenue =
                evt.venue ?? evt.address ?? "Jivandhara Campus, Palitana";
              const bannerImg =
                evt.image ??
                evt.banner_url ??
                pickFrom(FALLBACK_EVENT_BANNER, String(evt.id), i);

              // Standardized object to pass into detail and rsvp modals
              const modalObject = {
                ...evt,
                title: displayTitle,
                event_date: evt.start_date ?? evt.date ?? evt.event_date,
                category: displayCategory,
                short_description: displaySummary,
                banner_url: bannerImg,
                venue: displayVenue,
                start_time: startTime,
                end_time: endTime,
              };

              return (
                <Reveal key={evt.id ?? i} variant="up" delay={i * 60}>
                  <article className="shadow-xs group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EAE4D6] bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
                    {/* Event Image Banner */}
                    <div className="relative h-44 w-full overflow-hidden bg-[#F4EFE6] sm:h-52">
                      <img
                        src={bannerImg}
                        alt={displayTitle}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80" />

                      {/* Top-Left Category Badge */}
                      <span className="shadow-xs absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-[#1F2937] backdrop-blur-md">
                        <HeartHandshake className="h-3 w-3 text-amber-600" />
                        {displayCategory}
                      </span>

                      {/* Top-Right Date Badge */}
                      {displayDate && (
                        <span className="absolute right-3 top-3 rounded-full bg-white px-3 py-1 text-xs font-black text-[#0F4C81] shadow-md">
                          {displayDate}
                        </span>
                      )}
                    </div>

                    {/* Card Content Body */}
                    <div className="flex flex-1 flex-col p-5">
                      {/* Serif Event Title matching the design */}
                      <h3 className="line-clamp-1 font-serif text-xl font-bold text-[#A66E19] transition-colors group-hover:text-amber-700">
                        {displayTitle}
                      </h3>

                      {/* Short Summary */}
                      {displaySummary && (
                        <p className="mt-2 line-clamp-2 min-h-[2.25rem] text-xs leading-relaxed text-[#64748B]">
                          {displaySummary}
                        </p>
                      )}

                      {/* Divider */}
                      <div className="my-4 border-t border-[#F1EFE9]" />

                      {/* Time & Venue Metadata */}
                      <div className="space-y-1.5 text-xs text-[#64748B]">
                        {(Boolean(startTime) || Boolean(endTime)) && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                            <span>
                              {startTime}
                              {endTime ? ` – ${endTime}` : ""}
                            </span>
                          </div>
                        )}

                        {displayVenue && (
                          <div className="flex items-center gap-2 truncate">
                            <MapPin className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                            <span className="truncate">{displayVenue}</span>
                          </div>
                        )}
                      </div>

                      {/* Card Action Buttons (Footer) */}
                      <div className="mt-auto flex items-center gap-3 pt-5">
                        <button
                          type="button"
                          onClick={() => setDetailEvent(modalObject)}
                          className="shadow-xs flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#DCD5C6] bg-white px-3 py-2 text-xs font-bold text-[#334155] transition-all hover:border-amber-400 hover:bg-[#F9F7F2]"
                        >
                          <Eye className="h-3.5 w-3.5 text-amber-500" />
                          View Details
                        </button>

                        {activeTab === "upcoming" && (
                          <button
                            type="button"
                            onClick={() => setRsvpEvent(modalObject)}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-3 py-2 text-xs font-extrabold text-white shadow-sm transition-all hover:from-amber-600 hover:to-amber-700 active:scale-95"
                          >
                            <Ticket className="h-3.5 w-3.5" />
                            Register / RSVP
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>

      {/* Render Detail Modal */}
      {detailEvent && (
        <EventDetailModal
          open={Boolean(detailEvent)}
          onOpenChange={val => {
            if (!val) {
              setDetailEvent(null);
            }
          }}
          event={detailEvent}
          onRSVP={evt => setRsvpEvent(evt)}
        />
      )}

      {/* Render RSVP Registration Modal */}
      {rsvpEvent && (
        <EventRegistrationModal
          open={Boolean(rsvpEvent)}
          onOpenChange={val => {
            if (!val) {
              setRsvpEvent(null);
            }
          }}
          event={rsvpEvent}
        />
      )}
    </PageShell>
  );
}
